import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getViolationRiderName } from '../utils/violations';
import './EventInviteModal.css';

// Приглашения на участие в событии. Список — гонщики с подключенной интеграцией
// ROUVY, еще не зарегистрированные на событие (Api::EventInvitationsController).
// Приглашение приходит гонщику уведомлением, принять его — значит зарегистрироваться.
// Список подгружается страницами по мере прокрутки (infinite scroll)
const EventInviteModal = ({ event, onClose }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // Выбор сохраняется при подгрузке страниц и смене поиска
  const [selected, setSelected] = useState(new Set());
  // Приглашенные в этом окне — чтобы отметить их без перезагрузки списка
  const [sentIds, setSentIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);

  // Поиск отправляем после паузы в наборе
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams({ page });
        if (search) params.append('q', search);

        const response = await axios.get(`/api/events/${event.id}/invitations?${params.toString()}`);
        if (cancelled) return;

        const loaded = response.data.users || [];
        const { current_page: current, total_pages: totalPages, total_count: total } = response.data.pagination;

        // Первая страница (новый поиск) заменяет список, следующие дописываются.
        // Пагинация по offset: если между запросами кто-то зарегистрировался,
        // строки могут сдвинуться — дубли отбрасываем
        setUsers(prev => {
          if (page === 1) return loaded;
          const known = new Set(prev.map(u => u.id));
          return [...prev, ...loaded.filter(u => !known.has(u.id))];
        });
        setHasMore(current < totalPages);
        setTotalCount(total);
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.error || 'load_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [event.id, page, search]);

  // Новый поиск — список снова с начала
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [search]);

  // Метка в конце списка: как только она показалась — грузим следующую страницу.
  // Наблюдатель пересоздается после каждой загрузки, поэтому если список
  // не заполнил окно и метка все еще видна, подгрузка продолжится сама
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading || !hasMore || error) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setPage(p => p + 1);
      },
      { root: bodyRef.current, rootMargin: '0px 0px 120px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasMore, error, users.length]);

  const isInvited = (user) => user.invited || sentIds.has(user.id);
  const selectableLoaded = users.filter(u => !isInvited(u));
  const allLoadedSelected = selectableLoaded.length > 0 && selectableLoaded.every(u => selected.has(u.id));
  // Пока грузится первая страница, показываем заглушку вместо старого списка
  const initialLoading = loading && page === 1;

  const toggleUser = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleLoaded = () => {
    setSelected(prev => {
      const next = new Set(prev);
      selectableLoaded.forEach(u => (allLoadedSelected ? next.delete(u.id) : next.add(u.id)));
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;

    setSubmitting(true);
    setError('');
    setResult('');

    try {
      const ids = Array.from(selected);
      const response = await axios.post(`/api/events/${event.id}/invitations`, { user_ids: ids });
      setSentIds(prev => new Set([...prev, ...ids]));
      setSelected(new Set());
      setResult(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'send_error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const errorText = error === 'load_error'
    ? t('eventInvite.loadError')
    : error === 'send_error' ? t('eventInvite.sendError') : error;

  return (
    <div className="event-invite-overlay" onClick={handleOverlayClick}>
      <div className="event-invite-modal">
        <div className="event-invite-header">
          <div>
            <h2>{t('eventInvite.title')}</h2>
            <div className="event-invite-subtitle">{event.name}</div>
          </div>
          <button type="button" className="event-invite-close" onClick={onClose}>×</button>
        </div>

        <div className="event-invite-toolbar">
          <div className="event-invite-hint">{t('eventInvite.hint')}</div>
          <input
            type="search"
            className="event-invite-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('eventInvite.searchPlaceholder')}
          />
          <div className="event-invite-list-meta">
            {selectableLoaded.length > 0 ? (
              <label className="event-invite-select-all">
                <input type="checkbox" checked={allLoadedSelected} onChange={toggleLoaded} />
                {t('eventInvite.selectLoaded')}
              </label>
            ) : <span />}
            {!initialLoading && totalCount > 0 && (
              <span className="event-invite-found">{t('eventInvite.found')}: {totalCount}</span>
            )}
          </div>
        </div>

        <div className="event-invite-body" ref={bodyRef}>
          {initialLoading ? (
            <div className="event-invite-message">{t('common.loading')}</div>
          ) : users.length === 0 ? (
            <div className="event-invite-message">
              {search ? t('eventInvite.notFound') : t('eventInvite.empty')}
            </div>
          ) : (
            users.map(user => {
              const invited = isInvited(user);
              return (
                <label
                  key={user.id}
                  className={`event-invite-item ${invited ? 'event-invite-item-invited' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={invited || selected.has(user.id)}
                    disabled={invited}
                    onChange={() => toggleUser(user.id)}
                  />
                  <img
                    src={getUserAvatarUrl(user.avatar)}
                    onError={handleUserAvatarError}
                    alt=""
                    className="event-invite-avatar"
                  />
                  <span className="event-invite-user">
                    <span className="event-invite-name">{getViolationRiderName(user, language)}</span>
                    <span className="event-invite-nickname">@{user.nickname}</span>
                  </span>
                  {invited && <span className="event-invite-badge">{t('eventInvite.invited')}</span>}
                </label>
              );
            })
          )}
          {!initialLoading && users.length > 0 && (
            <>
              {loading && <div className="event-invite-message">{t('common.loading')}</div>}
              <div ref={sentinelRef} className="event-invite-sentinel" />
            </>
          )}
        </div>

        <div className="event-invite-footer">
          <div className="event-invite-status">
            {error ? (
              <span className="event-invite-error">{errorText}</span>
            ) : result ? (
              <span className="event-invite-result">{result}</span>
            ) : (
              <span>{t('eventInvite.selected')}: {selected.size}</span>
            )}
          </div>
          <button
            type="button"
            className="event-invite-submit"
            onClick={handleSubmit}
            disabled={submitting || selected.size === 0}
          >
            {submitting ? t('eventInvite.sending') : t('eventInvite.send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EventInviteModal;
